/**
 * Meta seed data — will be replaced by DB-driven data later.
 * Sourced from public tournament results / Untapped-style aggregated stats.
 */

export type Tier = 'S' | 'A' | 'B' | 'C';
export type Trend = 'rising' | 'stable' | 'falling';
export type ManaColor = 'W' | 'U' | 'B' | 'R' | 'G' | 'C';

export interface Archetype {
    name: string;
    colors: ManaColor[];
    tier: Tier;
    fieldShare: number;   // %
    winRate: number;       // %
    trend: Trend;
    keyCards: string[];
    matches: number;
}

export interface MetaCard {
    name: string;
    inHandWR: number;
    playedWR: number;
    includedWR: number;
    totalGames: number;
    colors: ManaColor[];
}

// Color display helpers
export const COLOR_PIPS: Record<ManaColor, { bg: string; text: string; label: string }> = {
    W: { bg: 'bg-amber-100', text: 'text-amber-900', label: 'W' },
    U: { bg: 'bg-blue-500', text: 'text-white', label: 'U' },
    B: { bg: 'bg-gray-800', text: 'text-gray-200', label: 'B' },
    R: { bg: 'bg-red-500', text: 'text-white', label: 'R' },
    G: { bg: 'bg-green-600', text: 'text-white', label: 'G' },
    C: { bg: 'bg-gray-400', text: 'text-gray-900', label: 'C' },
};

export const TIER_META: Record<Tier, { label: string; color: string; bg: string; border: string }> = {
    S: { label: 'S', color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30' },
    A: { label: 'A', color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30' },
    B: { label: 'B', color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30' },
    C: { label: 'C', color: 'text-gray-400', bg: 'bg-gray-500/15', border: 'border-gray-500/30' },
};

// ─── Standard Meta Seed ─────────────────────────────────────────

export const STANDARD_META: Archetype[] = [
    { name: 'Mono-White Aggro', colors: ['W'], tier: 'S', fieldShare: 14.0, winRate: 56.9, trend: 'rising', keyCards: ['Hopeful Initiate', 'Thalia, Guardian of Thraben', 'Adeline, Resplendent Cathar', 'Brave the Elements'], matches: 28000 },
    { name: 'Jeskai Artifacts', colors: ['W', 'U', 'R'], tier: 'S', fieldShare: 5.0, winRate: 56.9, trend: 'stable', keyCards: ['Simulacrum Synthesizer', 'Wrath of the Skies', 'Glass Casket'], matches: 10000 },
    { name: 'Boros Burn', colors: ['R', 'W'], tier: 'A', fieldShare: 7.2, winRate: 55.1, trend: 'stable', keyCards: ['Lightning Strike', 'Play with Fire', 'Monastery Swiftspear'], matches: 14400 },
    { name: '4-Color Reanimator', colors: ['W', 'U', 'B', 'G'], tier: 'A', fieldShare: 3.9, winRate: 56.4, trend: 'falling', keyCards: ['Atraxa, Grand Unifier', 'Invasion of Ikoria'], matches: 7800 },
    { name: 'Mono-Green Landfall', colors: ['G'], tier: 'A', fieldShare: 5.6, winRate: 55.3, trend: 'rising', keyCards: ['Lotus Cobra', 'Scute Swarm', 'Tireless Provisioner'], matches: 11200 },
    { name: 'Orzhov Skeletons', colors: ['W', 'B'], tier: 'A', fieldShare: 4.1, winRate: 56.2, trend: 'stable', keyCards: ['Skeleton Crew', 'Cruel Somnophage'], matches: 8200 },
    { name: 'Mono-Red Aggro', colors: ['R'], tier: 'A', fieldShare: 4.5, winRate: 54.3, trend: 'stable', keyCards: ['Monastery Swiftspear', 'Kumano Faces Kakkazan', 'Feldon, Ronom Excavator'], matches: 9000 },
    { name: 'Sultai Reanimator', colors: ['U', 'B', 'G'], tier: 'B', fieldShare: 3.2, winRate: 55.4, trend: 'stable', keyCards: ['Atraxa, Grand Unifier', 'Liliana of the Veil'], matches: 6400 },
    { name: 'Izzet Elementals', colors: ['U', 'R'], tier: 'B', fieldShare: 3.6, winRate: 55.2, trend: 'rising', keyCards: ['Risen Reef', 'Omnath, Locus of the Roil'], matches: 7200 },
    { name: 'Mono-Black Demons', colors: ['B'], tier: 'B', fieldShare: 5.0, winRate: 52.8, trend: 'falling', keyCards: ['Archfiend of the Dross', 'Sheoldred, the Apocalypse'], matches: 10000 },
    { name: 'Izzet Lesson', colors: ['U', 'R'], tier: 'B', fieldShare: 4.2, winRate: 53.1, trend: 'stable', keyCards: ['Inquisitive Glimmer', 'Galvanic Iteration'], matches: 8400 },
    { name: 'Jeskai Artifacts', colors: ['W', 'U', 'R'], tier: 'B', fieldShare: 3.6, winRate: 52.5, trend: 'falling', keyCards: ['Simulacrum Synthesizer', 'Third Path Iconoclast'], matches: 7200 },
    { name: 'Mono-White Lifegain', colors: ['W'], tier: 'C', fieldShare: 3.5, winRate: 51.2, trend: 'falling', keyCards: ["Ajani's Pridemate", 'Voice of the Blessed', 'Lunarch Veteran'], matches: 7000 },
    { name: 'Azorius Control', colors: ['W', 'U'], tier: 'C', fieldShare: 3.0, winRate: 51.8, trend: 'stable', keyCards: ['Teferi, Hero of Dominaria', 'Supreme Verdict', 'Memory Deluge'], matches: 6000 },
    { name: 'Golgari Midrange', colors: ['B', 'G'], tier: 'C', fieldShare: 2.8, winRate: 51.0, trend: 'falling', keyCards: ['Glissa Sunslayer', 'Vraska, Golgari Queen'], matches: 5600 },
];

export const STANDARD_CARDS: MetaCard[] = [
    { name: 'Ojer Pakpatiq, Deepest Epoch', inHandWR: 71.7, playedWR: 58.7, includedWR: 58.7, totalGames: 620, colors: ['U'] },
    { name: 'Inquisitive Glimmer', inHandWR: 64.6, playedWR: 59.7, includedWR: 59.7, totalGames: 590, colors: ['U'] },
    { name: 'Virtue of Strength', inHandWR: 64.4, playedWR: 52.6, includedWR: 52.6, totalGames: 570, colors: ['G'] },
    { name: 'Secluded Starforge', inHandWR: 64.3, playedWR: 53.5, includedWR: 53.5, totalGames: 520, colors: ['C'] },
    { name: 'Thalia, Guardian of Thraben', inHandWR: 63.1, playedWR: 57.2, includedWR: 57.2, totalGames: 4800, colors: ['W'] },
    { name: 'Sheoldred, the Apocalypse', inHandWR: 62.8, playedWR: 55.4, includedWR: 55.4, totalGames: 3200, colors: ['B'] },
    { name: 'Adeline, Resplendent Cathar', inHandWR: 62.5, playedWR: 57.8, includedWR: 57.8, totalGames: 5100, colors: ['W'] },
    { name: 'Atraxa, Grand Unifier', inHandWR: 61.9, playedWR: 56.1, includedWR: 56.1, totalGames: 2800, colors: ['W', 'U', 'B', 'G'] },
    { name: 'Monastery Swiftspear', inHandWR: 61.2, playedWR: 55.6, includedWR: 55.6, totalGames: 6200, colors: ['R'] },
    { name: 'Simulacrum Synthesizer', inHandWR: 60.8, playedWR: 56.9, includedWR: 56.9, totalGames: 3500, colors: ['U'] },
    { name: 'Lightning Strike', inHandWR: 59.4, playedWR: 54.2, includedWR: 54.2, totalGames: 8100, colors: ['R'] },
    { name: 'Lotus Cobra', inHandWR: 59.1, playedWR: 55.3, includedWR: 55.3, totalGames: 2900, colors: ['G'] },
];

// ─── Pioneer Meta Seed ──────────────────────────────────────────

export const PIONEER_META: Archetype[] = [
    { name: 'Rakdos Midrange', colors: ['B', 'R'], tier: 'S', fieldShare: 12.5, winRate: 55.8, trend: 'stable', keyCards: ['Thoughtseize', 'Fatal Push', 'Kroxa, Titan of Death\'s Hunger'], matches: 25000 },
    { name: 'Izzet Phoenix', colors: ['U', 'R'], tier: 'S', fieldShare: 9.8, winRate: 55.2, trend: 'rising', keyCards: ['Arclight Phoenix', 'Lightning Axe', 'Treasure Cruise'], matches: 19600 },
    { name: 'Azorius Control', colors: ['W', 'U'], tier: 'A', fieldShare: 7.2, winRate: 53.8, trend: 'stable', keyCards: ['Supreme Verdict', 'Teferi, Hero of Dominaria', 'Absorb'], matches: 14400 },
    { name: 'Mono-Green Devotion', colors: ['G'], tier: 'A', fieldShare: 6.5, winRate: 54.1, trend: 'falling', keyCards: ['Nykthos, Shrine to Nyx', 'Cavalier of Thorns', 'Storm the Festival'], matches: 13000 },
    { name: 'Abzan Greasefang', colors: ['W', 'B', 'G'], tier: 'B', fieldShare: 4.8, winRate: 52.6, trend: 'stable', keyCards: ['Greasefang, Okiba Boss', 'Parhelion II'], matches: 9600 },
    { name: 'Boros Heroic', colors: ['R', 'W'], tier: 'B', fieldShare: 5.1, winRate: 53.2, trend: 'rising', keyCards: ['Monastery Swiftspear', 'Illuminator Virtuoso'], matches: 10200 },
];

export const PIONEER_CARDS: MetaCard[] = [
    { name: 'Thoughtseize', inHandWR: 63.2, playedWR: 56.1, includedWR: 55.8, totalGames: 12000, colors: ['B'] },
    { name: 'Fatal Push', inHandWR: 61.5, playedWR: 55.3, includedWR: 54.9, totalGames: 11500, colors: ['B'] },
    { name: 'Arclight Phoenix', inHandWR: 65.1, playedWR: 55.2, includedWR: 55.2, totalGames: 9800, colors: ['R'] },
    { name: 'Nykthos, Shrine to Nyx', inHandWR: 62.8, playedWR: 54.1, includedWR: 54.1, totalGames: 6500, colors: ['C'] },
];

// ─── Format index ────────────────────────────────────────────────

export function getMetaForFormat(formatKey: string): { archetypes: Archetype[]; cards: MetaCard[] } {
    switch (formatKey) {
        case 'standard': return { archetypes: STANDARD_META, cards: STANDARD_CARDS };
        case 'pioneer': return { archetypes: PIONEER_META, cards: PIONEER_CARDS };
        default: return { archetypes: [], cards: [] };
    }
}
